import React, { useEffect, useRef } from 'react';

export function IntroducingBar() {
  const trackRef = useRef<HTMLDivElement>(null);
  const segmentRef = useRef<HTMLSpanElement>(null);
  
  // Animation state refs (not in React state to avoid re-renders)
  const offsetX = useRef(0);
  const velocity = useRef(28); // Idle speed in px/s
  const targetVelocity = useRef(28);
  const segmentWidth = useRef(0);
  const rafId = useRef<number>();
  const idleTimeout = useRef<number>();
  
  // Scroll tracking
  const lastScrollY = useRef(window.scrollY);
  const lastScrollTime = useRef(Date.now());
  
  // Check for reduced motion preference
  const prefersReducedMotion = useRef(
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const track = trackRef.current;
    const segment = segmentRef.current;
    if (!track || !segment) return;

    // Measure segment width
    const updateSegmentWidth = () => {
      const width = segment.getBoundingClientRect().width;
      if (width > 0) {
        segmentWidth.current = width;
      }
    };

    // ResizeObserver for dynamic width updates
    const resizeObserver = new ResizeObserver(updateSegmentWidth);
    resizeObserver.observe(segment);

    // Animation loop with requestAnimationFrame
    let lastTime = performance.now();
    
    const animate = (currentTime: number) => {
      const deltaTime = (currentTime - lastTime) / 1000; // Convert to seconds
      lastTime = currentTime;

      // Smooth velocity towards target (exponential smoothing)
      const smoothing = 0.1; // Higher = faster response
      velocity.current += (targetVelocity.current - velocity.current) * smoothing;

      // Update offset based on velocity
      offsetX.current += velocity.current * deltaTime;

      // Seamless loop without gaps: wrap in both directions with subpixel precision
      if (segmentWidth.current > 0) {
        while (offsetX.current >= segmentWidth.current) {
          offsetX.current -= segmentWidth.current;
        }
        while (offsetX.current < 0) {
          offsetX.current += segmentWidth.current;
        }
      }

      // Apply transform
      track.style.transform = `translate3d(${-offsetX.current}px, 0, 0)`;

      rafId.current = requestAnimationFrame(animate);
    };

    // Initialize after fonts are loaded
    const initializeAnimation = async () => {
      await document.fonts.ready;
      
      requestAnimationFrame(() => {
        updateSegmentWidth();
        
        // Start with small negative offset to prevent visible gap
        if (segmentWidth.current > 0) {
          offsetX.current = 0;
        }
        
        // Only start animation if width is measured
        if (segmentWidth.current > 0) {
          rafId.current = requestAnimationFrame(animate);
        }
      });
    };

    initializeAnimation();

    // Scroll listener for velocity tracking
    const handleScroll = () => {
      if (prefersReducedMotion.current) return;

      const currentScrollY = window.scrollY;
      const currentTime = Date.now();
      
      const deltaY = currentScrollY - lastScrollY.current;
      const deltaTime = currentTime - lastScrollTime.current;
      
      if (deltaTime === 0) return;
      
      // Calculate scroll velocity in px/s
      const scrollVelocity = (deltaY / deltaTime) * 1000;
      
      // Map scroll velocity to marquee speed
      const minSpeed = 28; // Idle speed
      const maxSpeed = 350; // Max speed on fast scroll
      const scrollInfluence = 0.15; // How much scroll affects speed
      
      const speed = Math.abs(scrollVelocity) * scrollInfluence;
      const clampedSpeed = Math.min(Math.max(speed, minSpeed), maxSpeed);
      
      // Apply direction: scrolling down = move left (positive), up = move right (negative)
      targetVelocity.current = scrollVelocity > 0 ? clampedSpeed : -clampedSpeed;
      
      lastScrollY.current = currentScrollY;
      lastScrollTime.current = currentTime;

      // Reset idle timeout
      if (idleTimeout.current) {
        clearTimeout(idleTimeout.current);
      }
      
      // Return to idle speed after 300ms of no scroll
      idleTimeout.current = window.setTimeout(() => {
        targetVelocity.current = prefersReducedMotion.current ? 0 : 28;
      }, 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup
    return () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
      if (idleTimeout.current) {
        clearTimeout(idleTimeout.current);
      }
      window.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, []);

  const textContent = (
    <>
      {Array(15).fill(null).map((_, i) => (
        <React.Fragment key={i}>
          INTRODUCING<span className="opacity-30 px-3">|</span>
        </React.Fragment>
      ))}
    </>
  );

  return (
    <div 
      className="relative z-[45] isolate py-2 overflow-hidden"
      style={{ backgroundColor: 'hsl(0, 0%, 3%)' }}
      aria-hidden="true"
    >
      <div 
        ref={trackRef}
        className="flex whitespace-nowrap will-change-transform"
        style={{ transform: 'translateZ(0)', willChange: 'transform' }}
      >
        <span 
          ref={segmentRef}
          className="font-sans uppercase tracking-[0.3em] text-xs font-light text-accent-a/70"
        >
          {textContent}
        </span>
        <span className="font-sans uppercase tracking-[0.3em] text-xs font-light text-accent-a/70">
          {textContent}
        </span>
        <span className="font-sans uppercase tracking-[0.3em] text-xs font-light text-accent-a/70">
          {textContent}
        </span>
        <span className="font-sans uppercase tracking-[0.3em] text-xs font-light text-accent-a/70">
          {textContent}
        </span>
        <span className="font-sans uppercase tracking-[0.3em] text-xs font-light text-accent-a/70">
          {textContent}
        </span>
        <span className="font-sans uppercase tracking-[0.3em] text-xs font-light text-accent-a/70">
          {textContent}
        </span>
      </div>
    </div>
  );
}
